import React from "react";
import { currencyFormatter, DataQuality } from "@sia/style-guide";
import { fetchWages, fetchCompanyDetail, fetchNogaDetail } from "./dataFetching";

function extractActiveFilters(salaryFilters, filterId, isInitialLoad) {
  const DEFAULT_ITEMS = [];
  if (isInitialLoad) {
    return DEFAULT_ITEMS;
  }
  let filter = salaryFilters.filters.find((e) => e.id === filterId);
  if (filter) {
    let items = filter.items.filter((e) => e.active).map((e) => e.id);
    return items;
  }
  return DEFAULT_ITEMS;
}

export function extractRequestBodyFromFilters(salaryFilters, isInitialLoad) {
  let years = extractActiveFilters(salaryFilters, "surveyYears", isInitialLoad);
  const nogaCodes = extractActiveFilters(salaryFilters, "nogaCode", isInitialLoad);
  const officeSizeIds = extractActiveFilters(salaryFilters, "officeSize", isInitialLoad);
  const greaterRegions = extractActiveFilters(salaryFilters, "greaterRegion", isInitialLoad);
  const employmentLevelIds = extractActiveFilters(salaryFilters, "levelId", isInitialLoad);
  const specXfuncIds = extractActiveFilters(salaryFilters, "functionId", isInitialLoad);
  const ageCategories = extractActiveFilters(salaryFilters, "ageCategory", isInitialLoad);
  const sex = extractActiveFilters(salaryFilters, "sex", isInitialLoad);
  let body = {
    years,
    nogaCodes,
    officeSizeIds,
    greaterRegions,
    employmentLevelIds,
    specXfuncIds,
    ageCategories,
    sex,
  };

  return body;
}

function activateDefaultNogaCode(filtersCopy, nogaFilterIndex) {
  const ARCHI_OFFICE_NOGA_CODE = 711101;
  filtersCopy.filters[nogaFilterIndex].items = filtersCopy.filters[nogaFilterIndex].items.map((option) => ({
    ...option,
    active: option.id === ARCHI_OFFICE_NOGA_CODE,
  }));
  return filtersCopy;
}

async function setInitialActiveNoga(filtersCopy, languageKey, nogaFilterIndex, companyAccess) {
  let onError = (err) => console.log(err);
  let companyDetail = await fetchCompanyDetail(onError, companyAccess);
  let { nogaId } = companyDetail.data[0];
  let nogaDetails = await fetchNogaDetail(languageKey, nogaId, onError);
  let { nogaCode } = nogaDetails.data;
  if (nogaFilterIndex !== -1) {
    filtersCopy.filters[nogaFilterIndex].items = filtersCopy.filters[nogaFilterIndex].items.map((option) => ({
      ...option,
      active: option.id === nogaCode,
    }));
    const noActiveNogas = filtersCopy.filters[nogaFilterIndex].items.every((item) => !item.active);
    // If the company doesn't have noga
    // Activate The Archi office as default selected noga
    if (noActiveNogas) {
      activateDefaultNogaCode(filtersCopy, nogaFilterIndex);
    }
  }
  return filtersCopy;
}

async function setInitialActiveEmployeeFunctions(filtersCopy, languageKey, companyAccess) {
  let onError = (err) => console.log(err);
  let requestBody = extractRequestBodyFromFilters(filtersCopy, false);
  let wagesData = await fetchWages(languageKey, requestBody, onError, companyAccess);
  let { context } = wagesData.data;
  let nextAvailableFilters = context.nextAvailableFilters;
  let nextAvailableFucntionIds = nextAvailableFilters.functionId.map((e) => e.id);
  let functionIdFilterIndex = filtersCopy.filters.findIndex((e) => e.id === "functionId");
  if (functionIdFilterIndex !== -1) {
    filtersCopy.filters[functionIdFilterIndex].items = filtersCopy.filters[functionIdFilterIndex].items.map((option) => ({
      ...option,
      active: nextAvailableFucntionIds.includes(option.id),
    }));
  }
  return filtersCopy;
}

function setInitialActiveSurveyYear(filtersCopy) {
  let surveyYearsFilterIndex = filtersCopy.filters.findIndex((e) => e.id === "surveyYears");
  let currentYear = new Date().getFullYear();
  filtersCopy.filters[surveyYearsFilterIndex].items = filtersCopy.filters[surveyYearsFilterIndex].items
    .filter((e) => e.id === currentYear)
    .map((e) => ({ ...e, active: true }));
  return filtersCopy;
}

export async function updateFilters(filters, submitedFilters, nextAvailableFilters, isInitialLoad, isMasterKeyValid, languageKey) {
  let filtersCopy = { ...filters };

  if (isInitialLoad) {
    filtersCopy.allAvailabeFilters = nextAvailableFilters;
  }

  filtersCopy.filters = filtersCopy.filters.map((filter) => {
    let filterCopy = { ...filter };
    filterCopy.items = filtersCopy.allAvailabeFilters[filter.id].map((item) => {
      let dropDownOption = {};
      if (filter.id === "surveyYears") {
        dropDownOption = { id: item, label: item, active: submitedFilters[filter.id].includes(item) };
        return dropDownOption;
      }
      let filterIds = nextAvailableFilters[filter.id].map((e) => e.id);
      dropDownOption = {
        id: item.id,
        label: item.value,
        name: item.value,
        active: submitedFilters[filter.id].includes(item.id),
        disabled: !filterIds.includes(item.id),
      };
      return dropDownOption;
    });

    return filterCopy;
  });
  let nogaFilterIndex = filtersCopy.filters.findIndex((e) => e.id === "nogaCode");
  if (isInitialLoad && isMasterKeyValid) {
    filtersCopy = await setInitialActiveNoga(filtersCopy, languageKey, nogaFilterIndex, isMasterKeyValid);
    filtersCopy = await setInitialActiveEmployeeFunctions(filtersCopy, languageKey, isMasterKeyValid);
    filtersCopy = setInitialActiveSurveyYear(filtersCopy);
    return { ...filtersCopy, newest: true, isInitialLoad: false };
  } else if (isInitialLoad && !isMasterKeyValid) {
    filtersCopy = activateDefaultNogaCode(filtersCopy, nogaFilterIndex);
    filtersCopy = await setInitialActiveEmployeeFunctions(filtersCopy, languageKey, isMasterKeyValid);
    filtersCopy = setInitialActiveSurveyYear(filtersCopy);
    return { ...filtersCopy, newest: true, isInitialLoad: false };
  }

  return { ...filtersCopy, newest: false, isInitialLoad: false };
}
// ---------------------------
// Shared helpers

function sortFunctionsAlphabetically(data) {
  return [...data].sort((a, b) => a.functionName.localeCompare(b.functionName));
}

// ---------------------------
// Chart functions

export function parseSalaryChartData(data, translate, isMasterKeyValid) {
  if (data.length === 0) {
    return [];
  }
  const sortedData = sortFunctionsAlphabetically(data);
  let parsedData = sortedData.map((funcItem) => {
    let averageGrossValueObject = funcItem.salaryAggregationsPerYear[0].metrics.find((e) => e.componentName === "gross_salary_year_calc");
    let averageObject = {
      key: funcItem.functionName,
      value: averageGrossValueObject.mean,
      group: translate("benchmarking.evaluations.salary.chart-legend.average"),
    };
    let myValueObject = {
      key: funcItem.functionName,
      value: averageGrossValueObject.companyValues.percentile50,
      group: translate("benchmarking.evaluations.salary.chart-legend.my-office"),
    };
    let data = [averageObject];
    if (isMasterKeyValid) {
      data.push(myValueObject);
    }
    return data;
  });
  return parsedData.flat().reverse();
}

// End of Chart functions
// ---------------------------

// ---------------------------
// Table functions

function formatAlternate(value) {
  return currencyFormatter.formatAlternate(value);
}

function formatDelta(value) {
  return value.toFixed(1);
}

function generateTableCells(funcItem, row, isMasterKeyValid) {
  let grossSalaryObject = funcItem.salaryAggregationsPerYear[0].metrics.find((e) => e.componentName === "gross_salary_year_calc");
  let baseSalaryObject = funcItem.salaryAggregationsPerYear[0].metrics.find((e) => e.componentName === "base_salary_rep");
  let addBenefitsObject = funcItem.salaryAggregationsPerYear[0].metrics.find((e) => e.componentName === "add_benefits_rep");
  let varBenefitsObject = funcItem.salaryAggregationsPerYear[0].metrics.find((e) => e.componentName === "var_benefits_rep");
  let objectsArray = [baseSalaryObject, addBenefitsObject, varBenefitsObject, grossSalaryObject];
  //let objectsArray = [baseSalaryObject, addBenefitsObject, grossSalaryObject];

  let cellObjects = objectsArray.map((object) => {
    let cells = [];
    if (isMasterKeyValid) {
      let averageCell = { value: formatAlternate(object.mean), className: "average", key: `${object.componentName}_average` };
      let ownValueCell = { value: formatAlternate(object.companyValues.percentile50), className: "own-value", key: `${object.componentName}_own` };
      let deltaCell = { value: formatDelta(object.companyValues.deltaPercentage), className: "delta", key: `${object.componentName}_delta` };
      cells.push(averageCell, ownValueCell, deltaCell);
    } else {
      let averageCellWithoutOwnValue = {
        value: <div className="average-without-own-value">{formatAlternate(object.mean)}</div>,
        className: "average",
        key: `${object.componentName}_average`,
      };
      cells.push(averageCellWithoutOwnValue);
    }
    return cells;
  });
  let flatenedObjectArray = cellObjects.flat();
  row.cells.push(...flatenedObjectArray);
}

export function parseSalaryTableData(data, isMasterKeyValid, translate) {
  if (data.length === 0) {
    return [];
  }
  const sortedData = sortFunctionsAlphabetically(data);
  let extraClasses = !isMasterKeyValid ? " without-own-value" : "";
  let parsedData = sortedData.map((funcItem) => {
    let grossSalaryObject = funcItem.salaryAggregationsPerYear[0].metrics.find((e) => e.componentName === "gross_salary_year_calc");
    let row = { cells: [], onRowClick: () => null };
    let funcCell = { value: funcItem.functionName, className: "left-align".concat(extraClasses), key: 0 };
    let numberOfEmployeesCell = {
      value: grossSalaryObject.quality.count,
      className: "center-align",
      key: 1,
    };

    let dataQualityCell = {
      value: (
        <DataQuality
          translate={translate}
          numberOfRecords={grossSalaryObject.quality.count}
          coefficient={grossSalaryObject.quality.coefficientOfVariation}
          compact
        />
      ),
      className: "data-quality",
      key: 2,
    };
    row.cells.push(funcCell, numberOfEmployeesCell, dataQualityCell);
    generateTableCells(funcItem, row, isMasterKeyValid);
    return row;
  });
  return parsedData;
}

// End of Table functions
// ---------------------------

// ---------------------------
// Boxplot functions

export function parseBoxplotData(data, isMasterKeyValid) {
  if (data.length === 0) {
    return { data: [], ownValues: [] };
  }
  const sortedData = sortFunctionsAlphabetically(data);
  let ownValues = [];
  let parsedData = sortedData.map((funcItem) => {
    let grossSalaryObject = funcItem.salaryAggregationsPerYear[0].metrics.find((e) => e.componentName === "gross_salary_year_calc");
    if (isMasterKeyValid) {
      let ownValue = grossSalaryObject.companyValues.percentile50;
      ownValues.push(ownValue);
    }
    let minObject = { group: funcItem.functionName, order: funcItem.functionId, value: grossSalaryObject.min };
    let maxObject = { group: funcItem.functionName, order: funcItem.functionId, value: grossSalaryObject.max };
    let percentile25 = { group: funcItem.functionName, order: funcItem.functionId, value: grossSalaryObject.boxPlot.percentile25 };
    let percentile50 = { group: funcItem.functionName, order: funcItem.functionId, value: grossSalaryObject.boxPlot.percentile50 };
    let percentile75 = { group: funcItem.functionName, order: funcItem.functionId, value: grossSalaryObject.boxPlot.percentile75 };
    return [minObject, percentile25, percentile50, percentile75, maxObject];
  });
  return { data: parsedData.flat().reverse(), ownValues };
}

// End of Boxplot functions
// ---------------------------

// ---------------------------
// Boxplot table

export function parseBoxplotTableData(data, isMasterKeyValid, translate) {
  if (data.length === 0) {
    return [];
  }
  const sortedData = sortFunctionsAlphabetically(data);
  let extraClasses = !isMasterKeyValid ? " without-own-value" : "";
  let parsedData = sortedData.map((funcItem) => {
    let grossSalaryObject = funcItem.salaryAggregationsPerYear[0].metrics.find((e) => e.componentName === "gross_salary_year_calc");
    let row = { cells: [], onRowClick: () => null };
    let funcCell = { value: funcItem.functionName, className: "left-align".concat(extraClasses), key: 0 };
    let numberOfEmployeesCell = {
      value: grossSalaryObject.quality.count,
      className: "center-align",
      key: 1,
    };
    let dataQualityCell = {
      value: (
        <DataQuality
          translate={translate}
          numberOfRecords={grossSalaryObject.quality.count}
          coefficient={grossSalaryObject.quality.coefficientOfVariation}
          compact
        />
      ),
      className: "data-quality",
      key: 2,
    };
    let p25Cell = { value: formatAlternate(grossSalaryObject.boxPlot.percentile25), className: "average", key: 3 };
    let p50Cell = { value: formatAlternate(grossSalaryObject.boxPlot.percentile50), className: "average", key: 4 };
    let p75Cell = { value: formatAlternate(grossSalaryObject.boxPlot.percentile75), className: "delta", key: 5 };
    let cells = [funcCell, numberOfEmployeesCell, dataQualityCell, p25Cell, p50Cell, p75Cell];
    if (isMasterKeyValid) {
      let ownvalueCell = {
        value: <div className="median-cell">{formatAlternate(grossSalaryObject.companyValues.percentile50)}</div>,
        className: "own-value",
        key: 6,
      };
      cells.push(ownvalueCell);
    }
    row.cells.push(...cells);
    return row;
  });
  return parsedData;
}

// End of Boxplot table
// ---------------------------
